"use client";

import { useQuery, UseQueryOptions } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { searchProducts } from "../api/search-products";
import { Product, ProductsMeta } from "@/types/product.types";
import { PaginatedProductResponseSchema } from "@/lib/validations/api.schema";

export function useSearchProducts(
  payload: {
    q?: string | undefined;
    page?: number | undefined;
    limit?: number | undefined;
    sort?: string | undefined;
    minPrice?: number | undefined;
    maxPrice?: number | undefined;
    categoryIds?: string[] | undefined;
    inStock?: boolean | undefined;
    [key: string]: unknown;
  },
  options?: Partial<UseQueryOptions<{ products: Product[]; meta: ProductsMeta }, AxiosError>>
) {
  return useQuery({
    queryKey: ["search-products", payload],
    queryFn: async () => {
      // Clean undefined keys to satisfy exactOptionalPropertyTypes
      const cleanPayload = Object.fromEntries(
        Object.entries(payload).filter(([_, v]) => v !== undefined)
      ) as Record<string, unknown>;
      const response = await searchProducts(cleanPayload);
      const result = PaginatedProductResponseSchema.safeParse(response);
      
      if (!result.success) {
        console.warn("Zod Validation Failed for useSearchProducts:", result.error);
        const rawData = (response as any)?.data?.data || [];
        const rawMeta = (response as any)?.data?.meta || { 
          total: 0, page: 1, limit: 12, totalPages: 1 
        };
        return { 
          products: rawData as Product[], 
          meta: rawMeta as ProductsMeta 
        };
      }

      return {
        products: result.data.data.data as Product[],
        meta: result.data.data.meta as ProductsMeta,
      };
    },
    ...options,
  });
}